import { injectable } from "inversify";
import mixpanel from "mixpanel-browser";
import config from "../config";

export type MixpanelBaseProps = {
    env: string | null;
}

export interface IMixPanelIdentity {
    shop_id?: number;
    shop_name?: string;
	shop_company?: string|null;
	human_role?: string|null;
    human_id?: number;
    human_email?: string;
}

export type MixpanelOptionalProperties = Record<string, any>;

@injectable()
class MixpanelService {
    constructor() {
        this.init();
    }

    private init(): void {
        if (config.mixPanelOFF) {
            console.log('[MixpanelService::init] Mixpanel is switched to OFF, see VITE_MIXPANEL_OFF in .env');
            return;
        }

        if (!config.mixPanelKey) {
            console.error('[MixpanelService::init] Mixpanel key not found');
            return;
        }

        try {
            mixpanel.init(config.mixPanelKey, {
                debug: false,
                track_pageview: false, // Custom page view tracking with browser URL (see PageUrlCoordinator)
                ignore_dnt: true, // ignores Do Not Track
                persistence: 'localStorage',
                record_mask_text_selector: '',
	            record_heatmap_data: true,
	            record_sessions_percent: 0, // Session replay recording disabled
            });
        } catch (error) {
            console.error('[MixpanelService::init] failed:', error);
        }
    }

    private setUserIdentity(identity: IMixPanelIdentity) {
        mixpanel.identify(`${identity.human_id}|${identity.shop_id}|${identity.shop_name}`);
        mixpanel.people.set(
            {
                $email: identity.human_email,
                $name: `${identity.shop_id}|${identity.shop_name}`,
	            'User ID': identity.human_id,
                'User Role': identity.human_role,
                'Shop ID': identity.shop_id,
                'Shop Name': identity.shop_name,
                'Shop Managed By': identity.shop_company,
            }
        );
    }

    /**
     * Registers the browser URL so Mixpanel heatmaps and other features use it instead of the iframe URL.
     * Must be called whenever the page changes.
     */
    registerPageUrl(fullUrl: string): void {
        try {
            if (config.mixPanelOFF) return;
            mixpanel.register({ $current_url: fullUrl });
        } catch (error) {
            console.error('[MixpanelService] registerPageUrl failed:', error);
        }
    }

    trackEvent(event: string, properties: MixpanelOptionalProperties, shopIdentifier: IMixPanelIdentity): void {
        try {
            if (config.mixPanelOFF) {
                console.info('[MixpanelService] trackEvent', event, properties, shopIdentifier);
                return;
            }
    
            this.setUserIdentity(shopIdentifier);
            const allProperties: MixpanelBaseProps & MixpanelOptionalProperties = {
                env: config.env ?? null,
                ...(properties || {}),
            };
            mixpanel.track(event, allProperties, {send_immediately: true});
        } catch (error) {
            console.error('[MixpanelService] trackEvent failed:', error);
        }
    }

}

export default MixpanelService;
